import { useState } from "react";
import { Button, Select, Option, Input } from "@material-tailwind/react";
import { t } from "i18next";
import { useWorkorderContext } from "../../../context/WorkorderContext";
import toast from "react-hot-toast";

interface MachineDraftRowProps {
  machines: EasyTech.Machine[];
  onSave: (data: { machine: EasyTech.Machine; notes: string }) => Promise<void>;
  onCancel: () => void;
}

/**
 * Composant pour l'ajout inline d'une machine dans un workorder
 * Affiche une ligne d'édition avec un sélecteur de machine et un champ de notes
 */
export default function MachineDraftRow({
  machines,
  onSave,
  onCancel
}: MachineDraftRowProps) {
  const [selectedMachineId, setSelectedMachineId] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Accéder au contexte pour vérifier les machines existantes
  const { getMachines } = useWorkorderContext();

  const handleSaveClick = async () => {
    // Réinitialiser l'erreur
    setError(null);

    if (!selectedMachineId) {
      setError(t("Veuillez sélectionner une machine"));
      return;
    }

    const selectedMachine = machines.find((m) => m.id === selectedMachineId);
    if (!selectedMachine) {
      setError(t("Machine non trouvée"));
      return;
    }

    // Vérifier si la machine existe déjà dans le workorder
    const existingMachines = getMachines();
    const machineExists = existingMachines.some(
      (m) => m.machine?.id === selectedMachineId
    );

    if (error) {
      toast.error(error);
      return;
    }

    if (machineExists) {
      setError(t("Cette machine est déjà présente dans l'intervention"));
      return;
    }

    setLoading(true);
    try {
      await onSave({
        machine: selectedMachine,
        notes: notes.trim(),
      });
    } catch (error) {
      console.error("Erreur lors de l'enregistrement de la machine :", error);

      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError(t("Une erreur est survenue lors de l'enregistrement"));
      }
    } finally {
      setLoading(false);
    }
  };
 
  return (
    <div className="p-4">
      <div className="flex flex-col gap-2">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-2">
          <div>
            <Select
              label={t("workorder:machines:selectMachine")}
              value={selectedMachineId}
              onChange={(value) => {
                setSelectedMachineId(value || "");
                setError(null);
              }}
              className="bg-white"
            >
              {machines.length > 0 ? (
                machines.map((machine) => (
                  <Option key={machine.id} value={machine.id}>
                    {`${machine.brand.label}${machine.type ? ` - ${machine.type.label}` : ""}${machine.serialNumber ? ` - ${machine.serialNumber}` : ""}`}
                  </Option>
                ))
              ) : (
                <Option disabled value="">
                  {t("workorder:machines:noData")}
                </Option>
              )}
            </Select>
          </div>
      
          <div>
            <Input
              label={t("workorder:machines:notes")}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="bg-white"
            />
          </div>
          <div className="flex justify-end gap-2 items-center">
            <Button
              variant="filled"
              size="md"
              color="white"
              className="btn-cancel normal-case font-thin border"
              onClick={onCancel}
              disabled={loading}
            >
              {t("common:cancel")}
            </Button>
            <Button
              variant="filled"
              className="bg-blue-500 normal-case font-thin"
              onClick={handleSaveClick}
              disabled={loading}
            >
              {loading ? t("common:saving") : t("common:save")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
